import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import LineEyeIcon from 'LuxKit/Icons/line/LineEyeIcon'
import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import useOffer from 'hooks/Offers/useOffer'
import { isBedbankOffer, isHotel } from 'lib/offer/offerTypes'
import LoadingBox from 'components/Common/Loading/LoadingBox'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  > * {
    height: 100%;
  }
`

const ThumbnailWrapper = styled.div`
  width: ${rem(44)};
  height: ${rem(58)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  flex-shrink: 0;
`

const SkeletonContainer = styled.div`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;
`

interface Props {
  offerId: string
  selectedTagSet: Set<string>
  onClick?: (propertyName: string, location?: string) => void
}

export default function ChatLandingViewedOfferTile({ offerId, selectedTagSet, onClick }: Props) {
  const intl = useIntl()
  const [offer, fetching] = useOffer(offerId, { requireSummaryOnly: true })

  const viewedLabel = useMemo(() => intl.formatMessage({
    id: 'NAkcWm438P8XyfKOECMc',
    defaultMessage: 'Previously viewed offer',
    description: 'Category label for previously viewed offer tile on the chat landing page',
  }), [intl])

  const isReady = !fetching && !!offer && isHotel(offer)
  const propertyName = isReady ? (isBedbankOffer(offer) ? offer.name : offer.property.name) : undefined
  const imageId = isReady ? offer.image?.id : undefined
  const rawLocation = isReady ? (isBedbankOffer(offer) ? offer.property.location : offer.location) : undefined
  const location = rawLocation && rawLocation !== propertyName ? rawLocation : undefined

  const tags = useMemo(() => {
    const parts: Array<string> = []
    if (propertyName) parts.push(propertyName)
    if (location) parts.push(location)
    return parts
  }, [propertyName, location])

  const selected = Boolean(tags.length > 0 && tags.every(t => selectedTagSet.has(t)))

  const handleClick = useCallback(() => {
    if (propertyName && onClick) {
      onClick(propertyName, location)
    }
  }, [propertyName, location, onClick])

  if (fetching) {
    return (
      <SkeletonContainer>
        <LoadingBox width={240} height={110} borderRadius="L" />
      </SkeletonContainer>
    )
  }

  if (!isReady) return null

  return (
    <Container onClick={handleClick}>
      <Sheet background="neutral-eight" border={selected ? 'neutral-one' : 'neutral-five'} borderRadius="L" padding={12}>
        <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
          <CaptionTextBlock
            variant="small"
            weight="bold"
            format="uppercase"
            colour="neutral-three"
            startIcon={<LineEyeIcon size="XS" />}
          >
            {viewedLabel}
          </CaptionTextBlock>
          <Group direction="horizontal" gap={12} verticalAlign="center" grow>
            {imageId && <ThumbnailWrapper>
              <ResponsiveImage id={imageId} alt={propertyName} fit="center" objectFit="cover" />
            </ThumbnailWrapper>}
            <Group direction="vertical" gap={2}>
              <BodyText variant="medium" weight="normal" lineClamp={2}>
                {propertyName}
              </BodyText>
              {location && <Caption variant="medium" colour="neutral-three" lineClamp={1}>
                {location}
              </Caption>}
            </Group>
          </Group>
        </Group>
      </Sheet>
    </Container>
  )
}
